// Pure helpers that turn the calculators' per-line cost-to-price `detail` and the
// project-level platform/retention lines into rows the internal split PDF renders.
// No recomputation: every figure here comes straight from the calculate-price
// response. The customer sheet stays blended; this build-up is internal only.

export interface BuildupLine {
  label: string;
  value: string;
}

export interface ProjectLevelPlatformComponent {
  label: string;
  monthly: number;
  contractTotal: number;
  perModuleMonthly?: number;
  moduleCount?: number;
  perUserMonthly?: number;
  users?: number;
  // Base-fee cost build-up (present on the platform base fee line).
  baseInfraMonthly?: number;
  infraBaseline?: number;
  perModuleInfraMonthly?: number;
  infraModuleCount?: number;
  baseSupportHours?: number;
  supportHourlyRate?: number;
  marginTarget?: number;
  baseCost?: number;
  baseFeeGross?: number;
}

export interface ProjectLevelRetention {
  tier: string;
  annual: number;
  years: number;
  contractTotal: number;
}

export interface ProjectLevelSplit {
  platform: {
    components: ProjectLevelPlatformComponent[];
    monthly: number;
    contractTotal: number;
  };
  retention: ProjectLevelRetention | null;
  months: number;
  total: number;
}

interface ProjectLevelInput {
  recurring?: {
    components?: Array<Record<string, unknown>>;
    platformMonthly?: number;
    months?: number;
    platformContractTotal?: number;
    contractTotal?: number;
  };
  total?: number;
}

const num = (v: unknown): number => (typeof v === 'number' ? v : 0);

const currency = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const perUnit = (n: number): string => `${currency(n)} / unit`;

const percent = (fraction: number): string => `${Math.round(fraction * 100)}%`;

const isRetention = (c: Record<string, unknown>): boolean =>
  'tier' in c && 'years' in c;

export function buildProjectLevelSplit(
  projectLevel?: ProjectLevelInput | null
): ProjectLevelSplit | null {
  const recurring = projectLevel?.recurring;
  const rawComponents = recurring?.components ?? [];
  if (rawComponents.length === 0) return null;

  const platformComponents: ProjectLevelPlatformComponent[] = [];
  let retention: ProjectLevelRetention | null = null;

  rawComponents.forEach(c => {
    if (isRetention(c)) {
      retention = {
        tier: String(c.tier ?? ''),
        annual: num(c.annual),
        years: num(c.years),
        contractTotal: num(c.contractTotal),
      };
      return;
    }
    platformComponents.push({
      label: String(c.label ?? ''),
      monthly: num(c.monthly),
      contractTotal: num(c.contractTotal),
      ...(c.perModuleMonthly !== undefined
        ? { perModuleMonthly: num(c.perModuleMonthly) }
        : {}),
      ...(c.moduleCount !== undefined ? { moduleCount: num(c.moduleCount) } : {}),
      ...(c.perUserMonthly !== undefined
        ? { perUserMonthly: num(c.perUserMonthly) }
        : {}),
      ...(c.users !== undefined ? { users: num(c.users) } : {}),
      ...(c.baseCost !== undefined
        ? {
            baseInfraMonthly: num(c.baseInfraMonthly),
            infraBaseline: num(c.infraBaseline),
            perModuleInfraMonthly: num(c.perModuleInfraMonthly),
            infraModuleCount: num(c.infraModuleCount),
            baseSupportHours: num(c.baseSupportHours),
            supportHourlyRate: num(c.supportHourlyRate),
            marginTarget: num(c.marginTarget),
            baseCost: num(c.baseCost),
            baseFeeGross: num(c.baseFeeGross),
          }
        : {}),
    });
  });

  return {
    platform: {
      components: platformComponents,
      monthly: num(recurring?.platformMonthly),
      contractTotal: num(recurring?.platformContractTotal),
    },
    retention,
    months: num(recurring?.months),
    total: num(projectLevel?.total),
  };
}

interface SourceRow {
  name?: string;
  format?: string;
  totalHours?: number;
  cost?: number;
  price?: number;
}

// The enriched per-source onboarding (NRE) detail (Part B): the inputs plus the
// derived hours, cost, and price, so a reader can reconstruct the math.
export interface NreMillDetail {
  id?: string | null;
  name?: string;
  format?: string;
  setupHours?: number;
  hoursPerReportType?: number;
  difficultyMultiplier?: number;
  reportTypeCount?: number;
  validationOverheadPct?: number;
  engineerRate?: number;
  marginTarget?: number;
  buildHours?: number;
  totalHours?: number;
  cost?: number;
  // The cost-model price (cost / (1 - margin)) before any override.
  computedPrice?: number;
  // The per-source manual NRE override; null/absent means the cost model stands.
  override?: number | null;
  price?: number;
}

const trimNum = (n: number): string => {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

// One row of a cost-to-price build-up: a label, the math that produced it (left
// blank on a pure subtotal), and the resulting figure. `kind` marks the running
// cost subtotal and the final price so the view can set those rows apart.
export interface BuildupStep {
  label: string;
  calc?: string;
  value: string;
  kind?: 'cost' | 'price';
}

// The onboarding (NRE) build-up as itemised rows: effort hours, the cost they
// imply, then the margin step to the price. A table reads far more clearly than
// the old single-line formula.
//
// When the source carries a manual NRE override, the override IS the price and
// the cost-model math does not apply, so the build-up shows a single override
// row rather than the effort / cost / margin derivation that did not produce it.
export function nreBuildupSteps(mill: NreMillDetail): BuildupStep[] {
  const override = mill.override;
  const hasOverride = override !== undefined && override !== null && Number(override) > 0;
  if (hasOverride) {
    return [
      {
        label: 'Manual override',
        calc: 'cost model not applied',
        value: currency(Number(override)),
      },
    ];
  }

  const setup = num(mill.setupHours);
  const build = num(mill.buildHours);
  const validation = num(mill.validationOverheadPct);
  const totalHours = num(mill.totalHours);
  const rate = num(mill.engineerRate);
  const cost = num(mill.cost);
  const margin = num(mill.marginTarget);
  const price = num(mill.price);
  // Build hours = reports/pipe x hours/report type x format difficulty, so a
  // reader sees why the build figure is what it is (and why it is zero when a
  // mill has no reports/pipe).
  const reports = trimNum(num(mill.reportTypeCount));
  const perType = trimNum(num(mill.hoursPerReportType));
  const diff = trimNum(num(mill.difficultyMultiplier));
  return [
    {
      label: 'Effort',
      calc: `(${trimNum(setup)} setup + ${trimNum(build)} build [${reports} x ${perType} hrs x ${diff} diff]) x ${trimNum(1 + validation)} validation`,
      value: `${trimNum(totalHours)} hrs`,
    },
    {
      label: 'Cost',
      calc: `${trimNum(totalHours)} hrs x $${trimNum(rate)}/hr`,
      value: currency(cost),
      kind: 'cost',
    },
    {
      label: 'Price',
      calc: `cost / (1 - ${trimNum(margin)} margin)`,
      value: currency(price),
      kind: 'price',
    },
  ];
}

// The enriched per-mill per-pipe (tracking charge) detail: the cost build-up
// components plus the per-mill inputs used to derive them, so a reader can
// reconstruct the cost-per-pipe and the resulting margin.
export interface PerPipeDetail {
  sourceId?: string | null;
  volume?: number;
  reportsPerPipe?: number;
  autoExtractRate?: number;
  minutesPerException?: number;
  hourlyRate?: number;
  qaOverheadPct?: number;
  marginTarget?: number;
  difficultyMultiplier?: number;
  computeMultiplier?: number;
  extractionCompute?: number;
  exceptionLabour?: number;
  qaOverhead?: number;
  storage?: number;
  costPerPipe?: number;
  computedPricePerPipe?: number;
  pricePerPipe?: number;
  floorPerPipe?: number;
  ceilingPerPipe?: number;
  bound?: string | null;
}

// The discount is treated as applied only when it shaves at least this much off
// the gross charge, so cent-level float noise never spawns a spurious row.
const DISCOUNT_EPSILON = 0.005;

// The per-pipe cost build-up as itemised rows: each cost component on its own
// line with the math behind it, then the cost-per-pipe subtotal and the margin
// step to the price-per-pipe. The per-document compute rate is derived from the
// extraction component so a reader can reconstruct each figure.
//
// `lineAmount` is the charge that actually lands on the quote for this line. It
// is post duration-discount, while the price-per-pipe above is the gross rate,
// so when it is lower than volume x gross we append the discount and the net
// rate. That reconciles the rows with the line total a reader sees.
export function perPipeBuildupSteps(detail: PerPipeDetail, lineAmount?: number): BuildupStep[] {
  const reports = num(detail.reportsPerPipe);
  const exceptionRate = 1 - num(detail.autoExtractRate);
  const minutes = num(detail.minutesPerException);
  const rate = num(detail.hourlyRate);
  const qaPct = num(detail.qaOverheadPct);
  const margin = num(detail.marginTarget);
  const extraction = num(detail.extractionCompute);
  const exceptions = num(detail.exceptionLabour);
  const qa = num(detail.qaOverhead);
  const storage = num(detail.storage);
  const cost = num(detail.costPerPipe);
  const price = num(detail.pricePerPipe);
  const volume = num(detail.volume);
  const difficulty = num(detail.difficultyMultiplier) || 1;
  // The compute multiplier scales the extraction line by source format (for
  // example scanned PDF at 2x). It is shown explicitly when not 1, the same way
  // the difficulty is shown on the exception line, so the figure reconciles.
  const computeMult = num(detail.computeMultiplier) || 1;
  const baseComputePerDoc = reports > 0 ? extraction / reports / computeMult : 0;
  const computePerDoc = reports > 0 ? extraction / reports : 0;
  const storagePerDoc = reports > 0 ? storage / reports : 0;
  // The format difficulty scales the exception labour, so it is shown in that
  // line's math when it is not 1, keeping the figure reconcilable.
  const diffSuffix = difficulty !== 1 ? ` x ${trimNum(difficulty)} diff` : '';
  // Same "diff" wording as the exception line: both are format-difficulty
  // factors (compute here, labour there), so they read consistently.
  const multSuffix = computeMult !== 1 ? ` x ${trimNum(computeMult)} diff` : '';

  const steps: BuildupStep[] = [
    {
      label: 'Extraction',
      calc: `${trimNum(reports)} docs x ${currency(computeMult !== 1 ? baseComputePerDoc : computePerDoc)}${multSuffix}`,
      value: currency(extraction),
    },
    {
      label: 'Exceptions',
      calc: `${trimNum(reports)} x ${percent(exceptionRate)} x ${trimNum(minutes)} min @ $${trimNum(rate)}/hr${diffSuffix}`,
      value: currency(exceptions),
    },
    { label: 'QA overhead', calc: `${percent(qaPct)} of labour`, value: currency(qa) },
    {
      label: 'Storage',
      calc: `${trimNum(reports)} docs x ${currency(storagePerDoc)}`,
      value: currency(storage),
    },
    { label: 'Cost per pipe', value: currency(cost), kind: 'cost' },
  ];

  const priceCalc = `cost / (1 - ${trimNum(margin)} margin)`;
  const gross = volume * price;
  const discounted =
    lineAmount !== undefined && volume > 0 && gross > 0 && lineAmount < gross - DISCOUNT_EPSILON;
  // The charged price-per-pipe is the result row unless a discount follows, in
  // which case it is an intermediate and the net per pipe is the result.
  const priceKind: BuildupStep['kind'] = discounted ? undefined : 'price';

  // When a per-pipe floor or ceiling binds, the margin step lands on the computed
  // price, then a bound row gives the price actually charged.
  if (detail.bound === 'floor' || detail.bound === 'ceiling') {
    const computed = num(detail.computedPricePerPipe) || price;
    steps.push({ label: 'Price per pipe', calc: priceCalc, value: currency(computed) });
    steps.push({
      label: detail.bound === 'floor' ? 'Per-pipe floor' : 'Per-pipe ceiling',
      calc: detail.bound === 'floor' ? `raised to ${currency(price)}` : `capped at ${currency(price)}`,
      value: currency(price),
      kind: priceKind,
    });
  } else {
    steps.push({ label: 'Price per pipe', calc: priceCalc, value: currency(price), kind: priceKind });
  }

  if (!discounted) return steps;

  const netRate = (lineAmount as number) / volume;
  const discountFraction = 1 - (lineAmount as number) / gross;
  steps.push({
    label: 'Duration discount',
    calc: `${percent(discountFraction)} off`,
    value: `-${currency(price - netRate)}`,
  });
  steps.push({
    label: 'Net per pipe',
    calc: `x ${volume.toLocaleString('en-US')} pipes = ${currency(lineAmount as number)}`,
    value: currency(netRate),
    kind: 'price',
  });
  return steps;
}

// The PipeFitter CRC scanning service per-pipe cost build-up: the crew day cost
// divided by throughput, then the margin step to the price-per-pipe.
export interface ScanningDetail {
  mode?: string;
  technicianDayRate?: number;
  scannerDayCost?: number;
  dayCost?: number;
  pipesPerDay?: number;
  scanMargin?: number;
  costPerPipe?: number;
  pricePerPipe?: number;
  volume?: number;
  days?: number;
}

export function scanningBuildupSteps(detail: ScanningDetail): BuildupStep[] {
  const tech = num(detail.technicianDayRate);
  const scanner = num(detail.scannerDayCost);
  const dayCost = num(detail.dayCost) || tech + scanner;
  const pipesPerDay = num(detail.pipesPerDay);
  const margin = num(detail.scanMargin);
  const volume = num(detail.volume);
  // Crew days are billed whole (rounded up). Each day costs the technician +
  // scanner day rates, so the total cost is days x day cost, then the margin
  // step to price.
  const days = num(detail.days) || (pipesPerDay > 0 ? Math.ceil(volume / pipesPerDay) : 0);
  const daysLabel = `${trimNum(days)} days`;
  const cost = days * dayCost;
  const price = cost / (1 - margin);
  return [
    {
      label: 'Crew days',
      calc: `${volume.toLocaleString('en-US')} pipes / ${trimNum(pipesPerDay)} per day`,
      value: daysLabel,
    },
    { label: 'Technician', calc: `${daysLabel} x ${currency(tech)}/day`, value: currency(days * tech) },
    { label: 'Scanner', calc: `${daysLabel} x ${currency(scanner)}/day`, value: currency(days * scanner) },
    { label: 'Cost', value: currency(cost), kind: 'cost' },
    {
      label: 'Price',
      calc: `cost / (1 - ${trimNum(margin)} margin)`,
      value: currency(price),
      kind: 'price',
    },
  ];
}

// The PipeFitter algorithm gain-share build-up: time saved valued at the
// firing-line rate, our captured share, the floor/cap, then the joint total.
export interface GainShareDetail {
  manualMinutes?: number;
  toolMinutes?: number;
  valuePerHour?: number;
  timeSavedMinutes?: number;
  valuePerJoint?: number;
  captureShare?: number;
  rawPerJoint?: number;
  pricePerJoint?: number;
  appliedBound?: string;
  floor?: number;
  cap?: number;
  volume?: number;
}

// `lineAmount` is the charge that lands on the quote, post duration-discount. The
// algorithm total above is the gross, so when the line is lower we append the
// discount and the net, reconciling the rows with the line total.
export function gainShareBuildupSteps(detail: GainShareDetail, lineAmount?: number): BuildupStep[] {
  const manual = num(detail.manualMinutes);
  const tool = num(detail.toolMinutes);
  const valuePerHour = num(detail.valuePerHour);
  const timeSaved = num(detail.timeSavedMinutes);
  const valuePerJoint = num(detail.valuePerJoint);
  const share = num(detail.captureShare);
  const raw = num(detail.rawPerJoint);
  const price = num(detail.pricePerJoint);
  const floor = num(detail.floor);
  const cap = num(detail.cap);
  const volume = num(detail.volume);
  const bound = detail.appliedBound;
  const boundCalc =
    bound === 'floor'
      ? `raised to ${currency(floor)} floor`
      : bound === 'cap'
        ? `capped at ${currency(cap)}`
        : `within ${currency(floor)} - ${currency(cap)}`;

  const gross = price * volume;
  const steps: BuildupStep[] = [
    {
      label: 'Time saved',
      calc: `${trimNum(manual)} - ${trimNum(tool)} min`,
      value: `${trimNum(timeSaved)} min/joint`,
    },
    {
      label: 'Value / joint',
      calc: `${trimNum(timeSaved)} min / 60 x $${trimNum(valuePerHour)}/hr`,
      value: currency(valuePerJoint),
    },
    { label: 'Captured share', calc: `${percent(share)} of value`, value: currency(raw) },
    { label: 'Price / joint', calc: boundCalc, value: currency(price), kind: 'cost' },
  ];

  const grossRow: BuildupStep = {
    label: 'Algorithm total',
    calc: `${currency(price)} x ${volume.toLocaleString('en-US')} joints`,
    value: currency(gross),
    kind: 'price',
  };

  const discounted =
    lineAmount !== undefined && gross > 0 && lineAmount < gross - DISCOUNT_EPSILON;
  if (!discounted) {
    steps.push(grossRow);
    return steps;
  }

  const discountFraction = 1 - (lineAmount as number) / gross;
  steps.push({ ...grossRow, kind: undefined });
  steps.push({
    label: 'Duration discount',
    calc: `${percent(discountFraction)} off`,
    value: `-${currency(gross - (lineAmount as number))}`,
  });
  steps.push({ label: 'Net total', value: currency(lineAmount as number), kind: 'price' });
  return steps;
}

// A per-joint, flat, or free-hook reporting / algorithm line. Shows the gross,
// then the duration discount and the net when the charged line is lower.
export interface PerUnitDetail {
  mode?: string;
  rate?: number;
  flat?: number;
  volume?: number;
}

export function perUnitLineBuildupSteps(
  detail: PerUnitDetail, lineAmount?: number
): BuildupStep[] | undefined {
  const mode = detail.mode;
  if (mode === 'free') {
    return [{ label: 'Free hook', calc: 'loss-leader', value: currency(0) }];
  }
  let gross: number;
  let grossRow: BuildupStep;
  if (mode === 'flat') {
    gross = num(detail.flat);
    grossRow = { label: 'Flat fee', value: currency(gross), kind: 'price' };
  } else if (mode === 'perJoint') {
    const rate = num(detail.rate);
    const volume = num(detail.volume);
    gross = rate * volume;
    grossRow = {
      label: 'Per joint',
      calc: `${currency(rate)} x ${volume.toLocaleString('en-US')} joints`,
      value: currency(gross),
      kind: 'price',
    };
  } else {
    return undefined;
  }

  const discounted =
    lineAmount !== undefined && gross > 0 && lineAmount < gross - DISCOUNT_EPSILON;
  if (!discounted) return [grossRow];

  const discountFraction = 1 - (lineAmount as number) / gross;
  return [
    { ...grossRow, kind: undefined },
    {
      label: 'Duration discount',
      calc: `${percent(discountFraction)} off`,
      value: `-${currency(gross - (lineAmount as number))}`,
    },
    { label: 'Net total', value: currency(lineAmount as number), kind: 'price' },
  ];
}

// A value-set line (e.g. the NDT per-inspection charge): the rate is value-set,
// The NDT per-inspection cost build-up: processing (scaled by data format) +
// storage + compute + AI, the cost-per-inspection subtotal, the margin step to
// the price, then any per-record floor or ceiling, times the volume, then the
// duration discount down to the net. Mirrors the per-pipe build-up so NDT reads
// the same way as Material Traceability.
export interface PerInspectionDetail {
  tier?: string;
  unit?: string;
  volume?: number;
  difficultyMultiplier?: number;
  // Shared record cost-model inputs (same as Material Traceability per-pipe).
  computeCostPerDoc?: number;
  autoExtractRate?: number;
  minutesPerException?: number;
  hourlyRate?: number;
  qaOverheadPct?: number;
  aiComputeCostPerDoc?: number;
  aiAnalysis?: number;
  computeMultiplier?: number;
  extractionCompute?: number;
  exceptionLabour?: number;
  qaOverhead?: number;
  marginTarget?: number;
  floorPerInspection?: number;
  ceilingPerInspection?: number;
  floorPerJoint?: number;
  ceilingPerJoint?: number;
  processing?: number;
  storage?: number;
  compute?: number;
  reportsPerInspection?: number;
  costPerReport?: number;
  costPerInspection?: number;
  computedPricePerInspection?: number;
  pricePerInspection?: number;
  costPerJoint?: number;
  computedPricePerJoint?: number;
  pricePerJoint?: number;
  impliedMargin?: number;
  bound?: string | null;
}

// The same cost-model build-up serves NDT (per inspection) and Coating (per
// joint); the unit noun and the cost/price field names are read generically.
const unitOf = (detail: PerInspectionDetail): string => detail.unit || 'inspection';
const recordCost = (detail: PerInspectionDetail): number =>
  num(detail.costPerInspection ?? detail.costPerJoint);
const recordPrice = (detail: PerInspectionDetail): number =>
  num(detail.pricePerInspection ?? detail.pricePerJoint);
const recordComputed = (detail: PerInspectionDetail): number =>
  num(detail.computedPricePerInspection ?? detail.computedPricePerJoint) || recordPrice(detail);

// The record cost-component rows, the same extraction + exception-labour + QA +
// storage build-up as Material Traceability per-pipe. Each record carries a
// number of report documents; most auto-extract, the rest need manual exception
// labour, scaled by the data-format difficulty.
function inspectionCostRows(detail: PerInspectionDetail): BuildupStep[] {
  const unit = unitOf(detail);
  const reports = num(detail.reportsPerInspection) || 1;
  const exceptionRate = 1 - num(detail.autoExtractRate);
  const minutes = num(detail.minutesPerException);
  const rate = num(detail.hourlyRate);
  const qaPct = num(detail.qaOverheadPct);
  const extraction = num(detail.extractionCompute);
  const exceptions = num(detail.exceptionLabour);
  const qa = num(detail.qaOverhead);
  const storage = num(detail.storage);
  const aiAnalysis = num(detail.aiAnalysis);
  const aiPerDoc = num(detail.aiComputeCostPerDoc);
  const difficulty = num(detail.difficultyMultiplier) || 1;
  const computeMult = num(detail.computeMultiplier) || 1;
  const diffSuffix = difficulty !== 1 ? ` x ${trimNum(difficulty)} diff` : '';
  // Compute and AI scale with the format multiplier; shown as "x N diff" to
  // match the exception line. Storage scales with the report count only.
  const multSuffix = computeMult !== 1 ? ` x ${trimNum(computeMult)} diff` : '';
  const baseComputePerDoc = reports > 0 ? extraction / reports / computeMult : 0;
  const storagePerDoc = reports > 0 ? storage / reports : 0;
  const rows: BuildupStep[] = [
    {
      label: 'Extraction',
      calc: `${trimNum(reports)} docs x ${currency(baseComputePerDoc)}${multSuffix}`,
      value: currency(extraction),
    },
    {
      label: 'Exceptions',
      calc: `${trimNum(reports)} x ${percent(exceptionRate)} x ${trimNum(minutes)} min @ $${trimNum(rate)}/hr${diffSuffix}`,
      value: currency(exceptions),
    },
    { label: 'QA overhead', calc: `${percent(qaPct)} of labour`, value: currency(qa) },
    {
      label: 'Storage',
      calc: `${trimNum(reports)} docs x ${currency(storagePerDoc)}`,
      value: currency(storage),
    },
  ];
  if (aiAnalysis > 0) {
    rows.push({
      label: 'AI analysis',
      calc: `${trimNum(reports)} docs x ${currency(aiPerDoc)}${multSuffix}`,
      value: currency(aiAnalysis),
    });
  }
  rows.push({ label: `Cost per ${unit}`, value: currency(recordCost(detail)), kind: 'cost' });
  return rows;
}

export function perInspectionBuildupSteps(
  detail: PerInspectionDetail, lineAmount?: number
): BuildupStep[] {
  const steps = inspectionCostRows(detail);
  const unit = unitOf(detail);
  const margin = num(detail.marginTarget) || num(detail.impliedMargin);
  const computed = recordComputed(detail);
  const price = recordPrice(detail);
  const volume = num(detail.volume);
  const priceCalc = `cost / (1 - ${trimNum(margin)} margin)`;

  if (detail.bound === 'floor' || detail.bound === 'ceiling') {
    steps.push({ label: `Price per ${unit}`, calc: priceCalc, value: currency(computed) });
    steps.push({
      label: detail.bound === 'floor' ? 'Per-record floor' : 'Per-record ceiling',
      calc: detail.bound === 'floor' ? `raised to ${currency(price)}` : `capped at ${currency(price)}`,
      value: currency(price),
      kind: 'price',
    });
  } else {
    steps.push({ label: `Price per ${unit}`, calc: priceCalc, value: currency(price), kind: 'price' });
  }

  const gross = volume * price;
  const discounted =
    lineAmount !== undefined && volume > 0 && gross > 0 && lineAmount < gross - DISCOUNT_EPSILON;
  if (!discounted) {
    steps.push({
      label: 'Total',
      calc: `${currency(price)} x ${volume.toLocaleString('en-US')}`,
      value: currency(gross),
      kind: 'price',
    });
    return steps;
  }

  const netRate = (lineAmount as number) / volume;
  const discountFraction = 1 - (lineAmount as number) / gross;
  steps.push({
    label: 'Duration discount',
    calc: `${percent(discountFraction)} off`,
    value: `-${currency(price - netRate)}`,
  });
  steps.push({
    label: `Net per ${unit}`,
    calc: `x ${volume.toLocaleString('en-US')} = ${currency(lineAmount as number)}`,
    value: currency(netRate),
    kind: 'price',
  });
  return steps;
}

function valueSetLines(detail: Record<string, unknown>): BuildupLine[] {
  const lines: BuildupLine[] = [
    { label: 'Rate', value: perUnit(num(detail.rate)) },
  ];
  if ('referenceCost' in detail) {
    lines.push({ label: 'Reference cost', value: perUnit(num(detail.referenceCost)) });
    lines.push({ label: 'Implied margin', value: percent(num(detail.impliedMargin)) });
  }
  return lines;
}

function algorithmLines(detail: Record<string, unknown>): BuildupLine[] {
  const bound = String(detail.appliedBound ?? '');
  const boundValue = bound === 'cap' ? num(detail.cap) : num(detail.floor);
  return [
    { label: 'Time saved', value: `${num(detail.timeSavedMinutes)} min / joint` },
    { label: 'Value per joint', value: currency(num(detail.valuePerJoint)) },
    { label: 'Captured share', value: percent(num(detail.captureShare)) },
    { label: 'Price per joint', value: currency(num(detail.pricePerJoint)) },
    { label: 'Applied bound', value: `${bound} (${currency(boundValue)})` },
  ];
}

function sourceLines(rows: SourceRow[]): BuildupLine[] {
  return rows.map(row => ({
    label: `${row.name ?? ''} (${row.format ?? ''})`,
    value: `${num(row.totalHours)} hrs, cost ${currency(num(row.cost))} to price ${currency(num(row.price))}`,
  }));
}

function costPerKey(detail: Record<string, unknown>): number {
  if ('costPerPipe' in detail) return num(detail.costPerPipe);
  if ('costPerInspection' in detail) return num(detail.costPerInspection);
  return num(detail.costPerUnit);
}

function pricePerKey(detail: Record<string, unknown>): number {
  if ('pricePerPipe' in detail) return num(detail.pricePerPipe);
  if ('pricePerInspection' in detail) return num(detail.pricePerInspection);
  return num(detail.pricePerUnit);
}

function costDrivenLines(detail: Record<string, unknown>): BuildupLine[] {
  const cost = costPerKey(detail);
  const price = pricePerKey(detail);
  const lines: BuildupLine[] = [
    { label: 'Cost', value: perUnit(cost) },
    { label: 'Price', value: perUnit(price) },
    { label: 'Resulting margin', value: percent(num(detail.impliedMargin)) },
  ];
  if (detail.belowMarginFloor === true) {
    lines.push({ label: 'Margin floor', value: 'applied' });
  }
  return lines;
}

function configNreLines(detail: Record<string, unknown>): BuildupLine[] {
  const lines: BuildupLine[] = [
    {
      label: 'Engineering',
      value: `cost ${currency(num(detail.engineeringCost))} to price ${currency(num(detail.engineeringPrice))}`,
    },
  ];
  if (num(detail.crewTraining) > 0) {
    lines.push({ label: 'Crew training', value: currency(num(detail.crewTraining)) });
  }
  return lines;
}

export function buildupLines(detail?: Record<string, unknown> | null): BuildupLine[] {
  if (!detail) return [];
  if ('algorithm' in detail) return algorithmLines(detail);
  if (detail.valueSet === true) return valueSetLines(detail);
  const sources = detail.sources ?? detail.mills ?? detail.vendors;
  if (Array.isArray(sources)) return sourceLines(sources as SourceRow[]);
  if ('costPerPipe' in detail || 'costPerUnit' in detail || 'costPerInspection' in detail) {
    return costDrivenLines(detail);
  }
  if ('engineeringCost' in detail) return configNreLines(detail);
  return [];
}
